import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Shield, Users, Building2, FileText, AlertTriangle, X, Save, Loader2, Search,
  Pause, Play, Pencil, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  superGetStats,
  superListFirms,
  superUpdateFirm,
  superListUsers,
  superGetPlans,
} from "@/lib/api";
import { formatIndianDate } from "@/lib/date";

export default function SuperAdmin() {
  const { isSuperAdmin, loading: authLoading } = useAuth();

  if (authLoading) {
    return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Loading…</div>;
  }
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <Panel />;
}

function Panel() {
  const [tab, setTab] = useState("firms");
  const [stats, setStats] = useState(null);
  const [firms, setFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // firm being edited
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f, u, p] = await Promise.all([
        superGetStats(), superListFirms(), superListUsers(), superGetPlans(),
      ]);
      setStats(s.data);
      setFirms(f.data || []);
      setUsers(u.data || []);
      setPlans(p.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredFirms = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return firms;
    return firms.filter((f) =>
      (f.name || "").toLowerCase().includes(q) ||
      (f.firm_id || "").toLowerCase().includes(q) ||
      (f.owner_email || "").toLowerCase().includes(q)
    );
  }, [firms, query]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  const handleQuickSuspend = async (firm) => {
    if (!window.confirm(`${firm.suspended ? "Unsuspend" : "Suspend"} ${firm.name}?`)) return;
    try {
      await superUpdateFirm(firm.firm_id, { suspended: !firm.suspended });
      toast.success(firm.suspended ? "Firm unsuspended" : "Firm suspended");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8" data-testid="super-admin-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield size={18} className="text-[#002FA7]" />
        <div className="label-eyebrow text-[11px] tracking-[0.2em] text-zinc-500">SUPER ADMIN · PLATFORM CONTROL</div>
      </div>
      <h1 className="font-heading text-3xl tracking-tight font-light mb-8">SaaS control plane</h1>

      {/* Global Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200 mb-8" data-testid="super-stats">
        <StatCard icon={Building2} label="Firms" value={stats?.total_firms} testId="stat-firms" />
        <StatCard icon={Users} label="Users" value={stats?.total_users} testId="stat-users" />
        <StatCard icon={FileText} label="Resolutions" value={stats?.total_resolutions} testId="stat-resolutions" />
        <StatCard icon={Building2} label="Companies" value={stats?.total_companies} testId="stat-companies" />
        <StatCard icon={BarChart3} label="Signups 30d" value={stats?.signups_last_30d} testId="stat-signups" />
        <StatCard icon={AlertTriangle} label="Suspended" value={stats?.suspended_firms} testId="stat-suspended" accent={stats?.suspended_firms > 0} />
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex border border-zinc-300 bg-white" data-testid="super-tabs">
          <button
            onClick={() => setTab("firms")}
            className={`px-4 py-2 text-sm font-medium border-r border-zinc-300 ${tab === "firms" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
            data-testid="tab-firms"
          >
            Firms ({firms.length})
          </button>
          <button
            onClick={() => setTab("users")}
            className={`px-4 py-2 text-sm font-medium ${tab === "users" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
            data-testid="tab-users"
          >
            Users ({users.length})
          </button>
        </div>
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === "firms" ? "Search firms by name, ID or owner email…" : "Search users by email or name…"}
            className="w-full pl-9 pr-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            data-testid="super-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-zinc-500 py-16 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : tab === "firms" ? (
        <FirmsTable
          firms={filteredFirms}
          onEdit={(f) => setEditing(f)}
          onQuickSuspend={handleQuickSuspend}
        />
      ) : (
        <UsersTable users={filteredUsers} firms={firms} />
      )}

      {editing && (
        <EditFirmModal
          firm={editing}
          plans={plans}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div className="bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} className={accent ? "text-rose-600" : "text-[#002FA7]"} />
        <span className="label-eyebrow text-[10px]">{label}</span>
      </div>
      <div className={`text-2xl font-light tracking-tight ${accent ? "text-rose-700" : "text-zinc-900"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function FirmsTable({ firms, onEdit, onQuickSuspend }) {
  if (firms.length === 0) {
    return <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">No firms.</div>;
  }
  return (
    <div className="border border-zinc-200 overflow-x-auto bg-white" data-testid="super-firms-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left">
            <Th>Firm</Th>
            <Th>Owner</Th>
            <Th>Plan</Th>
            <Th>Users</Th>
            <Th>Companies</Th>
            <Th>Resolutions</Th>
            <Th>Last activity</Th>
            <Th>Status</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {firms.map((f) => {
            const usersOver = f.user_count > f.max_users;
            const compsOver = f.company_count > f.max_companies;
            return (
              <tr key={f.firm_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`super-firm-row-${f.firm_id}`}>
                <td className="px-3 py-3">
                  <div className="font-medium text-zinc-900">{f.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-plex">{f.firm_id}</div>
                </td>
                <td className="px-3 py-3 text-zinc-700 text-[12px]">{f.owner_email || "—"}</td>
                <td className="px-3 py-3">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-300 text-zinc-700 bg-zinc-50">{f.plan}</span>
                </td>
                <td className={`px-3 py-3 font-mono-plex text-[12px] ${usersOver ? "text-rose-600 font-bold" : ""}`}>
                  {f.user_count}{f.pending_invites > 0 && <span className="text-amber-600">+{f.pending_invites}</span>} / {f.max_users}
                </td>
                <td className={`px-3 py-3 font-mono-plex text-[12px] ${compsOver ? "text-rose-600 font-bold" : ""}`}>
                  {f.company_count} / {f.max_companies}
                </td>
                <td className="px-3 py-3 font-mono-plex text-[12px]">{f.resolution_count}</td>
                <td className="px-3 py-3 text-[11px] text-zinc-500">{f.last_activity ? formatIndianDate(f.last_activity) : "—"}</td>
                <td className="px-3 py-3">
                  {f.suspended ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-rose-100 text-rose-700">SUSPENDED</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700">ACTIVE</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => onQuickSuspend(f)}
                    className="text-zinc-500 hover:text-rose-600 mr-3"
                    title={f.suspended ? "Unsuspend" : "Suspend"}
                    data-testid={`super-firm-suspend-${f.firm_id}`}
                  >
                    {f.suspended ? <Play size={13} /> : <Pause size={13} />}
                  </button>
                  <button
                    onClick={() => onEdit(f)}
                    className="text-[#002FA7] hover:text-[#002280]"
                    title="Edit"
                    data-testid={`super-firm-edit-${f.firm_id}`}
                  >
                    <Pencil size={13} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UsersTable({ users, firms }) {
  const firmsById = useMemo(() => Object.fromEntries(firms.map((f) => [f.firm_id, f])), [firms]);
  if (users.length === 0) {
    return <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">No users.</div>;
  }
  return (
    <div className="border border-zinc-200 overflow-x-auto bg-white" data-testid="super-users-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left">
            <Th>Email</Th>
            <Th>Name</Th>
            <Th>Current firm</Th>
            <Th>Firms</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`super-user-row-${u.user_id}`}>
              <td className="px-3 py-3 font-medium text-zinc-900">{u.email}</td>
              <td className="px-3 py-3 text-zinc-700 text-[12px]">{u.name || "—"}</td>
              <td className="px-3 py-3 text-[12px]">{firmsById[u.current_firm_id]?.name || u.current_firm_id || "—"}</td>
              <td className="px-3 py-3 font-mono-plex text-[12px]">{u.firm_count}</td>
              <td className="px-3 py-3 text-[11px] text-zinc-500">{u.created_at ? formatIndianDate(u.created_at) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-medium ${className}`}>{children}</th>;
}

function EditFirmModal({ firm, plans, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: firm.name,
    plan: firm.plan,
    max_users: firm.max_users,
    max_companies: firm.max_companies,
    suspended: firm.suspended,
    notes: firm.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const applyPlanDefaults = (plan) => {
    const defaults = plans[plan];
    setForm((f) => ({
      ...f,
      plan,
      ...(defaults ? { max_users: defaults.max_users, max_companies: defaults.max_companies } : {}),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await superUpdateFirm(firm.firm_id, form);
      toast.success("Firm updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-firm-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-300 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">Edit Firm</div>
            <h2 className="font-heading text-xl font-light tracking-tight">{firm.name}</h2>
            <div className="text-[11px] text-zinc-500 font-mono-plex mt-0.5">{firm.firm_id}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="edit-firm-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Name">
            <input
              type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="edit-firm-name"
            />
          </Field>

          <Field label="Plan (pick a tier or use custom limits below)">
            <div className="flex flex-wrap gap-2" data-testid="edit-firm-plans">
              {Object.keys(plans).map((p) => (
                <button
                  key={p} type="button" onClick={() => applyPlanDefaults(p)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
                    form.plan === p ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-zinc-300 bg-white text-zinc-700 hover:border-[#002FA7]"
                  }`}
                  data-testid={`edit-firm-plan-${p}`}
                >
                  {p} <span className="opacity-60 normal-case ml-1 text-[10px]">({plans[p].max_users}u / {plans[p].max_companies}c)</span>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Max Users (seats)">
              <input
                type="number" min={1} value={form.max_users}
                onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value || "1", 10) })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="edit-firm-max-users"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Current: {firm.user_count} member{firm.user_count !== 1 ? "s" : ""}{firm.pending_invites > 0 && ` + ${firm.pending_invites} pending`}</div>
            </Field>
            <Field label="Max Companies">
              <input
                type="number" min={0} value={form.max_companies}
                onChange={(e) => setForm({ ...form, max_companies: parseInt(e.target.value || "0", 10) })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="edit-firm-max-companies"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Current: {firm.company_count} compan{firm.company_count === 1 ? "y" : "ies"}</div>
            </Field>
          </div>

          <label className="flex items-center gap-3 p-3 border border-zinc-200 cursor-pointer">
            <input
              type="checkbox" checked={form.suspended}
              onChange={(e) => setForm({ ...form, suspended: e.target.checked })}
              className="accent-rose-600"
              data-testid="edit-firm-suspended"
            />
            <div>
              <div className="text-sm font-medium">Suspend this firm</div>
              <div className="text-[11px] text-zinc-500">Suspended firms cannot access any data via the API. Users see a 403 banner.</div>
            </div>
          </label>

          <Field label="Internal notes (admin only)">
            <textarea
              rows={3} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything you want to remember — billing context, contact person, etc."
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="edit-firm-notes"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-zinc-200 bg-zinc-50">
          <button onClick={onClose} className="swiss-btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="swiss-btn-primary" data-testid="edit-firm-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
